import { useQuery } from '@tanstack/react-query';
import React from 'react';
import styled from 'styled-components';

import { Icon, ObjectCard } from '@column/column-ui-kit';

import { FieldWrapper } from '~/app/pages/Transfers/_components/CopyRow';
import { Avatar } from '~/app/pages/Transfers/_components/Timeline/Avatar';
import { ROUTE } from '~/app/routes';
import { useNavigate } from '~/lib/navigation';
import { AccountNumber, BankAccountRepository } from '~/repositories/BankAccountRepository';
import { EntityRepository, EntityWithDetails } from '~/repositories/EntityRepository';
import { useSessionStore } from '~/stores/Session';
import { Text } from '~/styles';
import { BankAccountWithDetails } from '~/typings/API';

import { TransferParty, TransferPartyDetail } from './TransferParty';

interface BankAccountCardProps {
  /** Bank account to show. In the detailed variant it may be resolved from `accountNumberId` when absent. */
  bankAccountId?: string | null;
  /** Compact variant only: appended to the ID line as "· Available: {balance}". */
  availableBalance?: string;
  /**
   * `compact` (default) renders a minimal card with just the name and ID. `detailed` additionally shows
   * the routing/account number and the owning entity, in the richer party layout used on the transfer
   * batch views.
   */
  variant?: 'compact' | 'detailed';
  /**
   * Detailed variant: the specific account number used. Its routing/account number are shown instead of
   * the bank account defaults, and it resolves the bank account when no `bankAccountId` is supplied.
   */
  accountNumberId?: string | null;
}

const getBankAccountName = (bankAccount?: BankAccountWithDetails) =>
  bankAccount?.displayName || bankAccount?.description || 'Unnamed Bank Account';

const getEntityName = (entity?: EntityWithDetails) => {
  if (!entity) return undefined;
  if (entity.personDetails) {
    return `${entity.personDetails.firstName || ''} ${entity.personDetails.lastName || ''}`.trim();
  }
  if (entity.businessDetails) return entity.businessDetails.businessName;
  return undefined;
};

const CompactBankAccountCard: React.FC<{ bankAccountId?: string | null; availableBalance?: string }> = ({
  bankAccountId,
  availableBalance,
}) => {
  const navigate = useNavigate();
  const { currentPlatform } = useSessionStore();

  const {
    data: bankAccount,
    isLoading,
    error,
  } = useQuery<BankAccountWithDetails>({
    queryKey: [currentPlatform?.id, 'bank-account', bankAccountId],
    queryFn: () => BankAccountRepository.get({ id: bankAccountId ?? '' }),
    enabled: !!bankAccountId,
    gcTime: 30000,
    staleTime: 10000,
  });

  return (
    <ObjectCard
      error={error ? 'Failed to load bank account' : undefined}
      icon="Bank"
      isLoading={isLoading}
      primaryText={getBankAccountName(bankAccount)}
      id={{
        label: 'ID:',
        value: bankAccountId ?? '',
        suffix: availableBalance ? `· Available: ${availableBalance}` : undefined,
      }}
      onClick={() => bankAccountId && navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${bankAccountId}`)}
    />
  );
};

// Rendered inside a CopyRow FieldWrapper so it lines up with the sibling copy rows.
const EntityLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition:
    background 0.2s,
    box-shadow 0.2s;

  svg {
    --icon-size: 16px;
    --icon-color: ${({ theme }) => theme.secondary.blendToBackground(600)};
    flex-shrink: 0;
    transition: transform 0.2s;
  }

  &:hover {
    background: ${({ theme }) => theme.secondary.blendToBackground(50)};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)};
  }

  &:active svg {
    transform: translateX(2px);
    transition: transform 0.1s;
  }
`;

const DetailedBankAccountCard: React.FC<{ bankAccountId?: string | null; accountNumberId?: string | null }> = ({
  bankAccountId,
  accountNumberId,
}) => {
  const navigate = useNavigate();
  const { currentPlatform, isAuthorized } = useSessionStore();

  const { data: accountNumber } = useQuery<AccountNumber>({
    queryKey: [currentPlatform?.id, 'account-number', accountNumberId],
    queryFn: () => BankAccountRepository.getAccountNumber({ id: accountNumberId ?? '' }),
    enabled: !!accountNumberId,
    gcTime: 30000,
    staleTime: 10000,
  });

  // Batches can be configured with only an account number; resolve its bank account so the card still
  // renders. An explicit bankAccountId always wins.
  const resolvedBankAccountId = bankAccountId || accountNumber?.bankAccountId;

  const { data: bankAccount } = useQuery<BankAccountWithDetails>({
    queryKey: [currentPlatform?.id, 'bank-account', resolvedBankAccountId],
    queryFn: () => BankAccountRepository.get({ id: resolvedBankAccountId ?? '' }),
    enabled: !!resolvedBankAccountId,
    gcTime: 30000,
    staleTime: 10000,
  });

  // Roles without entity read access can't reach the entities API, so skip the request entirely and
  // let the Entity row below be omitted rather than firing a call that will fail.
  const canReadEntities = isAuthorized({ permission: 'entities', permissionLevel: 'read' });
  const entityId = bankAccount?.owners?.[0];
  const { data: entity } = useQuery<EntityWithDetails>({
    queryKey: [currentPlatform?.id, 'entity', entityId],
    queryFn: () => EntityRepository.get({ id: entityId ?? '' }),
    enabled: !!entityId && canReadEntities,
    retry: false,
    gcTime: 30000,
    staleTime: 10000,
  });

  // When a specific account number was requested, only ever show that one — falling back to the bank
  // account's default while the account-number query is loading (or if it fails) would display the wrong
  // account/routing numbers. The defaults are used only when no accountNumberId was supplied.
  const resolvedAccountNumber = accountNumberId ? accountNumber?.accountNumber : bankAccount?.defaultAccountNumber;
  const resolvedRoutingNumber = accountNumberId ? accountNumber?.routingNumber : bankAccount?.routingNumber;
  const entityName = getEntityName(entity);
  const entityType = entity?.type === 'PERSON' ? 'person' : 'business';

  const details: TransferPartyDetail[] = [];
  if (resolvedRoutingNumber) {
    details.push({ label: 'Routing Number', value: resolvedRoutingNumber, isMonospaced: true });
  }
  if (resolvedAccountNumber) {
    details.push({ label: 'Account Number', value: resolvedAccountNumber, isMonospaced: true });
  }
  if (entityId && entityName) {
    details.push({
      label: 'Entity',
      value: (
        <FieldWrapper>
          <EntityLink type="button" onClick={() => navigate(`${ROUTE.ENTITIES}/edit/${entityType}/${entityId}`)}>
            <Text as="span" size={14} weight="medium" color="foreground">
              {entityName}
            </Text>
            <Icon.ChevronRight />
          </EntityLink>
        </FieldWrapper>
      ),
    });
  }

  return (
    <TransferParty
      standalone
      icon={
        <Avatar size="large">
          <Icon.Wallet />
        </Avatar>
      }
      title={getBankAccountName(bankAccount)}
      subtitle={
        <Text as="span" size={12} weight="medium" color="lighter" monospace>
          {resolvedBankAccountId}
        </Text>
      }
      details={details}
      onClick={() => resolvedBankAccountId && navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${resolvedBankAccountId}`)}
    />
  );
};

export const BankAccountCard: React.FC<BankAccountCardProps> = ({
  variant = 'compact',
  bankAccountId,
  availableBalance,
  accountNumberId,
}) =>
  variant === 'detailed' ? (
    <DetailedBankAccountCard bankAccountId={bankAccountId} accountNumberId={accountNumberId} />
  ) : (
    <CompactBankAccountCard bankAccountId={bankAccountId} availableBalance={availableBalance} />
  );
